import { makeObservable, observable, runInAction } from 'mobx';
import { isEmpty, isNil } from 'lodash';
import { WebClient } from '@slack/web-api';
import firebase from 'firebase';
import { randomString, epochSecondsFloat } from '../../utils';
import { deploymentConfig } from '../../deployment-config';
import {
  db,
  fieldValueDelete,
  fieldValueArrayUnion,
} from '../../../platform/firebase-init';
import { loaderStatus } from '../../firestore-db/constants';
import { ElementId, ElementIdToElement } from '@tikka/basic-types';
import { AppUser, UserManager } from './user-manager';
import { UnitManager } from '../../catalog/db/unit-manager';
import { DisposerFn } from '../../basic-types-aside';

type CollectionReference = firebase.firestore.CollectionReference;
type QuerySnapshot = firebase.firestore.QuerySnapshot;

const { NOT_INITIATED, IN_PROGRESS, COMPLETE } = loaderStatus;

export interface CommentDTO {
  id?: ElementId; // COMMENT:[random 12 char string]
  content?: string; // the actual comment/message text
  author?: string; // user.id
  edited?: boolean; // true if modified after initial post
  timestamp?: number; // time of post or last update (seconds since unix epoch)
  // slackId: string // slack message id. presence indicates comment has been mirror to slack

  // these parent references are stuffed in during the load
  episodeKey?: string;
  elementId?: string;
  message?: string;
}

export interface CommentDictionary {
  [index: string]: CommentDTO;
}

export interface ConversationDTO {
  id?: string; // [episodeKey]-[elementId]
  episodeKey?: string;
  elementId?: ElementId;
  // comments: any // map of id -> CommentDTO
  comments?: CommentDictionary;
  commentList?: CommentDTO[];
  participants?: string[]; // list of user.id's
  assignee?: string; // user.id
  resolved?: boolean;
  slackThreadId?: string;
  timestamp?: number; // last updated
  // volumeSlug: string

  // stuffed in after load
  withMessages?: boolean;
}

// todo: factor this up with the script editor link generation
export const linkUrl = (conversation: ConversationDTO) => {
  const baseUrl = deploymentConfig.scriptEditorUrl;
  const linkPath = `/episodes/${conversation.episodeKey}/editor/${conversation.elementId}`;
  return `${baseUrl}${linkPath}`;
};

interface ConstructorOptions {
  episode?: string; // episodeKey
  assignee?: string; // user.id
  participant?: string; // user.id
}

export class ConversationManager {
  collectionRef: CollectionReference;
  listenMode = true;
  disposers: DisposerFn[] = [];

  episodeKey: string;
  assignee: string;
  participant: string;

  @observable.ref
  status: string = NOT_INITIATED;

  @observable.ref
  list: ConversationDTO[] = [];

  @observable.ref
  obj: ElementIdToElement<ConversationDTO> = {};

  @observable.ref
  currentUser: AppUser;

  @observable.ref
  trackingEnabled: boolean;

  @observable.ref
  mayUpdateTracking: boolean;

  @observable.ref
  baselineTimestamp: number;

  @observable.ref
  stateVersion = 1;

  constructor(options: ConstructorOptions = {}) {
    // TODO: confirm if this is actually compatible - was needed by masala-server/node build
    this.collectionRef = db.collection(
      'Element__conversations'
    ) as unknown as CollectionReference;
    // two modes for this loader, one fetching by episode and the other by user
    this.episodeKey = options.episode;
    this.assignee = options.assignee; // user.id
    this.participant = options.participant; // user.id
    makeObservable(this);
  }

  setUser(appUser: AppUser) {
    this.currentUser = appUser;
    if (appUser.isReviewer) {
      this.trackingEnabled = true;
      this.mayUpdateTracking = false;
    } else {
      this.trackingEnabled = false; // todo: honor unit level default
      this.mayUpdateTracking = true;
    }
  }

  setBaselineTimestamp(timestamp: number) {
    this.baselineTimestamp = timestamp;
  }

  setTracking(enabled: boolean) {
    if (!enabled && this.currentUser?.isReviewer) {
      throw Error(`setTracking(false) disallowed for external user`);
    }
    this.trackingEnabled = enabled;
  }

  // notification hook to be invoked imperatively when an element is updated within a script editor session
  // will have the side-effect of opening the discussion thread if tracking is enabled and not already open
  async elementRevised(elementId: ElementId) {
    if (this.trackingEnabled) {
      if (!this.episodeKey || !this.currentUser) {
        throw Error(
          `unexpected call to ConversationManager.elementRevised(${elementId}) w/o user or episode context`
        );
      }

      const conversationData = this.getConversation(elementId);
      if (conversationData.resolved) {
        console.log(`elementRevised(${elementId}) - marking unresolved `);
        conversationData.resolved = false;
        conversationData.withMessages = true;

        // todo: automatically treat currentUser as participant

        const episodeKey = this.episodeKey;
        const docRef = this.docRef({ episodeKey, elementId });
        await docRef.set(conversationData, { merge: true });
      }
    }
  }

  loadEpisode(key: string, listenMode = true) {
    this.episodeKey = key;
    this.listenMode = listenMode;
    this.load();
    // todo: set baselineTimestamp
  }

  // raw: true -> return null if no conversation record yet or not yet initialized
  //      false -> returns placeholder empty record if missing or not yet initialized
  getConversation(
    elementId: ElementId,
    raw = false,
    episodeKey = this.episodeKey
  ): ConversationDTO {
    const result = this.list.find(
      data => data.episodeKey === episodeKey && data.elementId === elementId
    );
    if (!raw && isNil(result)) {
      return {
        comments: {},
        commentList: [],
        resolved: true,
        elementId,
        episodeKey,
      };
    }
    return result;
  }

  // private
  buildDocId({
    episodeKey,
    elementId,
  }: {
    episodeKey: string;
    elementId: string;
  }) {
    // todo: give more thought to doc id's
    return `${episodeKey}-${elementId}`;
  }

  // private
  docRef({ episodeKey, elementId }: { episodeKey: string; elementId: string }) {
    const docId = this.buildDocId({ episodeKey, elementId });
    return this.collectionRef.doc(docId);
  }

  async initCollection() {
    await this.collectionRef.doc('!dummy').set({});
  }

  // async createConversation(conversationData) {
  //   console.log(`createConversation: ${JSON.stringify(conversationData)}`);
  //   const docId = this.buildDocId(conversationData);

  //   const {episodeKey, elementId, participants = [], assignee = null, resolved = false, comments = {}} = conversationData;
  //   const timestamp = epochSecondsFloat();
  //   const createData = {id: docId, episodeKey, elementId, participants, assignee, resolved, comments, timestamp};

  //   const docRef = this.collectionRef.doc(docId);
  //   await docRef.set(createData);
  // }

  async deleteConversation(elementId: string, episodeKey = this.episodeKey) {
    const docId = this.buildDocId({ episodeKey, elementId });
    const docRef = this.collectionRef.doc(docId);
    await docRef.delete();
  }

  async postComment(
    elementId: ElementId,
    author: string,
    content: string,
    episodeKey = this.episodeKey,
    suppressSlackNotification = false
  ) {
    const unit = UnitManager.getInstance().getById(episodeKey);
    console.log(
      `postComment: ${JSON.stringify({
        episodeKey,
        elementId,
        author,
        content,
      })}`
    );
    const commentId = `COMMENT:${randomString(12)}` as ElementId; // todo: someday revisit guid strategy?

    const timestamp = epochSecondsFloat();
    const commentData: CommentDTO = {
      id: commentId,
      author,
      content,
      timestamp,
    };

    // TODO jrw remove the (*) variants?
    const resolved =
      content.includes('/resolve') ||
      content.includes('/accept') ||
      content.includes('(accepted)') ||
      content.includes('(resolved)'); // hack to test logic

    const unslack = resolved || content.includes('/unslack');

    const docId = this.buildDocId({ episodeKey, elementId });
    const conversationData: ConversationDTO = {
      id: docId,
      episodeKey,
      elementId,
      comments: { [commentId]: commentData },
      resolved,
      timestamp,
    };

    // note, this only matches against current query.
    // desired handling of resolved conversations needs product level input.
    // currently they will have createdAt and assignee reset, but retain prior comments.
    const existingConversation = this.getConversation(
      elementId,
      true,
      episodeKey
    );
    if (isNil(existingConversation)) {
      conversationData.assignee = null;
    }
    const slackThreadModeOn = !!existingConversation?.slackThreadId;

    const joinedParticipants = [author];

    // todo: parsing and validation needs to be much more robust here
    let hasMentions: boolean = false;
    if (content.includes('@')) {
      const words = content.split(' ');
      const mentioned = words
        .filter(word => word.startsWith('@'))
        .map(word => word.replace('@', ''));

      if (isEmpty(mentioned)) {
        conversationData.assignee = null; // TODO is this right?
      } else {
        hasMentions = true;
        joinedParticipants.push(...mentioned);
        conversationData.assignee = mentioned[0];
      }
    }

    const slack = content.includes('/slack') || hasMentions;
    const turnOnSlackThreadMode = !slackThreadModeOn && slack && !unslack;
    const turnOffSlackThreadMode = slackThreadModeOn && unslack;

    // todo: review the need to cast this
    conversationData.participants = fieldValueArrayUnion(
      ...joinedParticipants
    ) as unknown as string[];

    const docRef = this.docRef({ episodeKey, elementId });
    await docRef.set(conversationData, { merge: true });

    const sendToSlack =
      !suppressSlackNotification && (slackThreadModeOn || slack);

    if (sendToSlack) {
      // const linkPath = `/episodes/${episodeKey}/${elementId}`;
      const linkUrl = unit.resolveEditorUrl(null /*default locale*/, elementId);
      const existingThreadId = isEmpty(existingConversation?.slackThreadId)
        ? null
        : existingConversation?.slackThreadId;
      console.log(
        `notifySlack - existingThreadId: ${existingThreadId}, linkUrl: ${linkUrl}`
      );
      // TODO when elevating to slack thread need to mention existing participants?
      const threadId = await this.notifySlack({
        threadId: existingThreadId,
        topic: `${unit.slug}/${elementId}`,
        linkUrl,
        author,
        content,
      });
      if (turnOnSlackThreadMode) {
        await docRef.update({ slackThreadId: threadId });
      }
    } else {
      console.log(`slack notifcation skipped for ${docId}`);
    }
    if (turnOffSlackThreadMode) {
      await docRef.update({ slackThreadId: fieldValueDelete() }); // TODO jrw why not set to null?
    }
  }

  // beware, if threads exist which have been first posted to a different slack channel
  // then the thread id must be reset before they'll show on the new channel
  async resetSlackThreadId(elementId: string, episodeKey = this.episodeKey) {
    const docRef = this.docRef({ episodeKey, elementId });
    await docRef.update({ slackThreadId: fieldValueDelete() });
  }

  async updateComment(
    elementId: string,
    commentId: string,
    content: string,
    episodeKey = this.episodeKey
  ) {
    const contentPath = `comments.${commentId}.content`;
    const timestampPath = `comments.${commentId}.timestamp`;
    const editedPath = `comments.${commentId}.edited`;
    const docRef = this.docRef({ episodeKey, elementId });
    await docRef.update({
      [contentPath]: content,
      [editedPath]: true,
      [timestampPath]: epochSecondsFloat(),
    });
  }

  async deleteComment(
    elementId: string,
    commentId: string,
    episodeKey = this.episodeKey
  ) {
    console.log(
      `deleteComment: ek: ${episodeKey}, ei: ${elementId}, ci: ${commentId}`
    );
    const timestamp = epochSecondsFloat();
    const commentPath = `comments.${commentId}`;
    const docRef = this.docRef({ episodeKey, elementId });
    await docRef.update({ [commentPath]: fieldValueDelete(), timestamp });
  }

  /** @return CollectionReference or DocumentReference of data to be loaded */
  // TODO: consider moving load option from constructor to here
  loadReference(options = {}) {
    console.log(
      `ConversationManager.loadReference: episode: ${this.episodeKey}, participant: ${this.participant}, assignee: ${this.assignee}`
    );
    const baseQuery = this.collectionRef; // don't fully hide the resolved threads // .where('resolved', '==', false);
    if (this.episodeKey) {
      return baseQuery.where('episodeKey', '==', this.episodeKey);
    } else {
      if (this.assignee) {
        return baseQuery.where('assignee', '==', this.assignee);
      } else {
        if (this.participant) {
          return baseQuery.where(
            'participants',
            'array-contains',
            this.participant
          );
        } else {
          // throw Error("ConversationsManager filter scope expected")
          return this.collectionRef.where('id', '==', '_none_');
        }
      }
    }
  }

  /**
   * @snapshot QuerySnapshot or DocumentSnapshot
   * @return model data
   */
  snapshotToData(snapshot: QuerySnapshot): ConversationDTO[] {
    // todo: at some point this will deserve some optimization
    const result: ConversationDTO[] = [];
    const objResult: ElementIdToElement<ConversationDTO> = {};
    snapshot.forEach(documentSnapshot => {
      const data: ConversationDTO = documentSnapshot.data();
      data.commentList = data.comments
        ? Object.values(data.comments).sort((a, b) => a.timestamp - b.timestamp)
        : [];
      // denormalized data here makes deleting easier
      data.commentList.forEach(comment => {
        comment.episodeKey = data.episodeKey;
        comment.elementId = data.elementId;
      });
      data.withMessages = true; // drives thread marker UI; we can assume true if included here

      objResult[data.elementId] = data;
      result.push(data);
    });
    this.list = result;
    this.obj = objResult;
    this.stateVersion++;
    return result;
  }

  load(options = {}): void {
    runInAction(() => (this.status = IN_PROGRESS));
    this.close();

    const handleResult = (result: QuerySnapshot) => {
      console.log(`um-handleResult`);
      runInAction(() => {
        this.list = this.snapshotToData(result);
        this.status = COMPLETE;
        console.log(`UserManager - status = COMPLETE`);
      });
    };

    const queryRef = this.loadReference(options);
    if (this.listenMode) {
      const unsubscribeFn = queryRef.onSnapshot(handleResult);
      this.disposers.push(unsubscribeFn);
    } else {
      queryRef.get().then(handleResult);
    }
  }

  close() {
    for (const disposer of this.disposers) {
      disposer();
    }
    this.disposers = [];
  }

  getStateVersion() {
    return this.status + '_' + this.stateVersion;
  }

  // note, doesn't appear to be needed for our current integration, but might be
  // useful in the future.
  // https://slack.dev/node-slack-sdk/getting-started
  // https://slack.dev/node-slack-sdk/web-api#exchange-an-oauth-grant-for-a-token
  async getSlackToken() {
    const clientId = process.env.SLACK_CLIENT_ID;
    const clientSecret = process.env.SLACK_CLIENT_SECRET;
    const code = '...';

    const result = await new WebClient().oauth.v2.access({
      client_id: clientId,
      client_secret: clientSecret,
      code,
    });
    console.log(`result token: ${result}`);
  }

  async helloSlack() {
    const web = new WebClient(deploymentConfig.slackApiToken);
    const channel = deploymentConfig.slackConversationsChannel;
    await web.chat.postMessage({
      channel: channel,
      text: 'how now brown cow',
    });
  }

  // private
  async notifySlack({
    threadId,
    topic,
    linkUrl,
    author,
    content,
  }: {
    threadId: string;
    topic: string;
    linkUrl: string;
    author: string;
    content: string;
  }) {
    const userManager = UserManager.getInstance();
    let slackifiedContent = userManager.slackifyMentions(content);
    //TODO jrw move someplace else?
    slackifiedContent = slackifiedContent.replace('/resolve', '(resolved)');
    slackifiedContent = slackifiedContent.replace('/accept', '(accepted)');
    slackifiedContent = slackifiedContent.replace('/slack', '(slack)');
    slackifiedContent = slackifiedContent.replace('/unslack', '(unslack)');

    console.log(`slackifiedContent: ${slackifiedContent}`);

    // const baseUrl = deploymentConfig.scriptEditorUrl;
    // note, doesn't seem like a leading '#' matters
    const channel = deploymentConfig.slackConversationsChannel;
    console.log(
      `slack channel: ${channel}, slack token: ${deploymentConfig.slackApiToken}`
    );
    const web = new WebClient(deploymentConfig.slackApiToken);

    var message = `_${author}_: ${slackifiedContent}`;
    if (!threadId) {
      // todo: refactor to use link (or conversation instance method)
      const link = `<${linkUrl}|${topic}>`;
      message = `${link}\n${message}`;
    }
    const result = await web.chat.postMessage({
      text: message,
      channel,
      thread_ts: threadId,
      unfurl_links: false,
    });
    console.log(`slack result: ${JSON.stringify(result)}`);
    const resultThreadId = result.ts;
    return resultThreadId;
  }

  private static instance: ConversationManager;

  public static getInstance(): ConversationManager {
    if (!ConversationManager.instance) {
      ConversationManager.instance = new ConversationManager();
    }
    return ConversationManager.instance;
  }
}
